import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusContext } from '../context/Status';
import { getCachedStatus, getLogo, getSystemName } from '../helpers';
import './AuthLayout.css';

const BrandIcon = ({ type }) => {
  if (type === 'route') {
    return (
      <svg viewBox='0 0 24 24' aria-hidden='true'>
        <circle cx='6' cy='6' r='2.2' />
        <circle cx='18' cy='7' r='2.2' />
        <circle cx='12' cy='18' r='2.2' />
        <path d='m8 7 8 0.2M7.2 8l3.8 8M16.8 9l-3.7 7' />
      </svg>
    );
  }
  if (type === 'stream') {
    return (
      <svg viewBox='0 0 24 24' aria-hidden='true'>
        <path d='M4 7h10M4 12h16M4 17h7' />
        <path d='m15.5 15.5 3 1.5-3 1.5z' />
      </svg>
    );
  }
  return (
    <svg viewBox='0 0 24 24' aria-hidden='true'>
      <path d='M12 3.5 19 6v5.2c0 4.3-2.8 7.7-7 9.3-4.2-1.6-7-5-7-9.3V6z' />
      <path d='m8.5 12 2.2 2.2 4.8-5' />
    </svg>
  );
};

const AuthLayout = ({
  title,
  subtitle,
  variant = 'login',
  kicker,
  children,
}) => {
  const { t, i18n } = useTranslation();
  const [statusState] = useContext(StatusContext);
  const status = statusState?.status || getCachedStatus();
  const logo = status.logo || getLogo();
  const systemName = status.system_name || getSystemName();
  const features = [
    {
      icon: 'route',
      title: t('auth.brand.routing'),
      text: t('auth.brand.routing_desc'),
    },
    {
      icon: 'stream',
      title: t('auth.brand.streaming'),
      text: t('auth.brand.streaming_desc'),
    },
    {
      icon: 'shield',
      title: t('auth.brand.secure'),
      text: t('auth.brand.secure_desc'),
    },
  ];

  return (
    <main className={`auth-page auth-page-${variant}`}>
      <label className='auth-language-switch'>
        <span>{t('header.language')}</span>
        <select
          value={(i18n.resolvedLanguage || i18n.language).split('-')[0]}
          onChange={(event) => i18n.changeLanguage(event.target.value)}
        >
          <option value='zh'>中文</option>
          <option value='en'>English</option>
        </select>
      </label>
      <div className='auth-grid-background' aria-hidden='true' />
      <div className='auth-glow auth-glow-one' aria-hidden='true' />
      <div className='auth-glow auth-glow-two' aria-hidden='true' />
      <div className='auth-layout'>
        <section className='auth-brand-panel'>
          <div className='auth-brand-content'>
            <div className='auth-brand-mark'>
              <span className='auth-logo-wrap'>
                <img src={logo} alt='' />
              </span>
              <div>
                <strong>{systemName}</strong>
                <span>{t('auth.brand.console')}</span>
              </div>
            </div>

            <div className='auth-brand-copy'>
              <span className='auth-brand-kicker'>
                {t('auth.brand.kicker')}
              </span>
              <h1>{t('auth.brand.title')}</h1>
              <p>{t('auth.brand.description')}</p>
            </div>

            <div className='auth-network' aria-hidden='true'>
              <svg viewBox='0 0 520 210' preserveAspectRatio='none'>
                <path
                  className='auth-network-path auth-path-one'
                  d='M34 158 C120 65 190 176 270 88 S420 20 486 74'
                />
                <path
                  className='auth-network-path auth-path-two'
                  d='M23 75 C105 128 170 25 258 104 S408 188 494 125'
                />
              </svg>
              <span className='auth-network-node node-one' />
              <span className='auth-network-node node-two' />
              <span className='auth-network-node node-three' />
              <span className='auth-network-core'>API</span>
            </div>

            <div className='auth-feature-list'>
              {features.map((feature, index) => (
                <div
                  className={`auth-feature auth-feature-${index + 1}`}
                  key={feature.title}
                >
                  <span>
                    <BrandIcon type={feature.icon} />
                  </span>
                  <div>
                    <strong>{feature.title}</strong>
                    <small>{feature.text}</small>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className='auth-brand-footer'>
            <span className='auth-live-dot' />
            {t('auth.brand.status')}
          </div>
        </section>

        <section className='auth-form-panel'>
          <div className='auth-mobile-brand'>
            <img src={logo} alt='' />
            <strong>{systemName}</strong>
          </div>
          <div className={`auth-card auth-card-${variant}`}>
            <header className='auth-card-header'>
              <span className='auth-card-kicker'>
                {kicker || t(`auth.${variant}.kicker`)}
              </span>
              <h2>{title}</h2>
              <p>{subtitle}</p>
            </header>
            <div className='auth-card-body'>{children}</div>
          </div>
          <p className='auth-form-caption'>{t('auth.brand.caption')}</p>
        </section>
      </div>
    </main>
  );
};

export default AuthLayout;
